import {IssueOpenedIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {useCallback} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import {BulkIssueIssueTypePicker} from '../bulk-pickers/BulkIssueIssueTypePicker'
import {LABELS} from '../constants/labels'
import type {SharedListHeaderActionProps} from '../types'
import type {ApplyIssueTypeBulkActionQuery} from './__generated__/ApplyIssueTypeBulkActionQuery.graphql'

type ApplyIssueTypeActionProps = {
  issueIds: string[]
} & Omit<SharedListHeaderActionProps, 'repo'>

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const ApplyIssueTypeQuery = graphql`
  query ApplyIssueTypeBulkActionQuery($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Issue {
        id
        actionIssueType: issueType {
          id
          name
          isEnabled
          description
          color
          pinnedFields {
            __typename
            ... on IssueFieldText {
              id
              name
              dataType
            }
            ... on IssueFieldSingleSelect {
              id
              name
              dataType
            }
            ... on IssueFieldDate {
              id
              name
              dataType
            }
            ... on IssueFieldNumber {
              id
              name
              dataType
            }
          }
        }
      }
    }
  }
`

export const ApplyIssueTypeBulkAction = ({issueIds, disabled, nested, ...rest}: ApplyIssueTypeActionProps) => {
  const {nodes: updatedIssueNodes} = useLazyLoadQuery<ApplyIssueTypeBulkActionQuery>(
    ApplyIssueTypeQuery,
    {ids: issueIds},
    {fetchPolicy: 'store-only'},
  )
  const selectedIssueTypes = (updatedIssueNodes || [])
    .filter(node => !!node)
    .map(node => {
      return node && node.actionIssueType ? node.actionIssueType : null
    })

  const activeIssueType = new Set(selectedIssueTypes.map(t => t?.id)).size === 1 ? selectedIssueTypes[0] || null : null

  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      if (nested) {
        return (
          <ActionList.Item
            data-testid={'bulk-set-issue-type-button'}
            disabled={disabled}
            {...anchorProps}
            role="menuitem"
          >
            <ActionList.LeadingVisual>
              <IssueOpenedIcon />
            </ActionList.LeadingVisual>
            {LABELS.setIssueType}
          </ActionList.Item>
        )
      }

      return (
        <Button
          data-testid={'bulk-set-issue-type-button'}
          disabled={disabled}
          leadingVisual={IssueOpenedIcon}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
        >
          {LABELS.setIssueType}
        </Button>
      )
    },
    [disabled, nested],
  )

  return (
    <BulkIssueIssueTypePicker
      readonly={false}
      activeIssueType={activeIssueType}
      anchorElement={anchorElement}
      nested={nested}
      {...rest}
    />
  )
}

try{ ApplyIssueTypeBulkAction.displayName ||= 'ApplyIssueTypeBulkAction' } catch {}